import { API_BASE_URL } from "./advancedSettings";

export interface TransferOffer {
  id: number;
  streamName: string;
  fromEmail: string;
  toEmail: string;
  createdAt: string;
  expiresAt: string;
}

export interface Transfers {
  outgoing: TransferOffer[];
  incoming: TransferOffer[];
}

export type OfferResult =
  | { status: "sent" }
  | { status: "reauthenticate" }
  | { status: "wait"; retryAfterSeconds: number }
  | { status: "problem"; message: string };

export type ActionResult = { ok: true; streamName?: string } | { ok: false; message: string };

const GENERIC = "Something went wrong. Please try again.";

const post = async (getToken: () => Promise<string>, action: string, body: unknown): Promise<Response> =>
  fetch(`${API_BASE_URL}/transfer/${action}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${await getToken()}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

/** The account's open offers: the ones it made and the ones addressed to its email. Throws when the API does not answer. */
export async function fetchTransfers(getToken: () => Promise<string>): Promise<Transfers> {
  const response = await fetch(`${API_BASE_URL}/transfer/Mine`, { headers: { Authorization: `Bearer ${await getToken()}` } });
  if (!response.ok) throw new Error(`Loading your transfers failed (${response.status})`);
  const body = await response.json();
  return { outgoing: body.outgoing ?? [], incoming: body.incoming ?? [] };
}

/** Offers one of the account's streams to an email address. The token must be fresh (the caller re-checks the password). Never throws. */
export async function offerStream(getToken: () => Promise<string>, streamName: string, toEmail: string): Promise<OfferResult> {
  try {
    const response = await post(getToken, "RequestTransfer", { streamName, toEmail });
    if (response.ok) return { status: "sent" };
    const body = await response.json().catch(() => ({}));
    if (response.status === 403) return { status: "reauthenticate" };
    if (response.status === 429 && body.retryAfterSeconds) return { status: "wait", retryAfterSeconds: Number(body.retryAfterSeconds) || 60 };
    return { status: "problem", message: String(body.message || GENERIC) };
  } catch (error) {
    console.error("Could not offer the stream:", error);
    return { status: "problem", message: GENERIC };
  }
}

const act = (action: "Accept" | "Decline" | "Cancel") => async (getToken: () => Promise<string>, id: number): Promise<ActionResult> => {
  try {
    const response = await post(getToken, action, { id });
    const body = await response.json().catch(() => ({}));
    if (response.ok) return { ok: true, streamName: body.streamName };
    return { ok: false, message: String(body.message || GENERIC) };
  } catch (error) {
    console.error(`Could not ${action.toLowerCase()} the transfer:`, error);
    return { ok: false, message: GENERIC };
  }
};

export const acceptTransfer = act("Accept");
export const declineTransfer = act("Decline");
export const cancelTransfer = act("Cancel");

/** Days left before an offer expires, never below 0. */
export const daysLeft = (expiresAt: string, now: Date = new Date()): number =>
  Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now.getTime()) / 86_400_000));
